/* One school trip: what it is, its temporary logins, and cancelling it
   (2026-10-28). The classic dashboard's trip detail view, in the console.
   Cancel is classic handleCancelTrip: the trip doc, then the backend cancel
   (which notifies parents), then the crew's notice — and parents again only
   if the backend's own notice never ran. */
import React, { useEffect, useRef, useState } from 'react';
import { doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  cancelTrip as cancelTripApi, isTripsApiConfigured, notifyTripParents, reissueTripConductor, reissueTripParent,
} from '../services/tripsApi';
import { ConsoleModal, fieldInput, ModalButtons, ModalField } from './Modal';
import { Chip, ChipTone, dayMonth, hhmm, plural, useAction, useToast } from './kit';
import type { FleetModel, Trip } from './data/model';

const TONE: Record<Trip['status'], ChipTone> = { confirmed: 'teal', live: 'teal', needs_driver: 'warn', draft: 'mute', done: 'ok', cancelled: 'mute' };

interface Login { uid: string; kind: 'conductor' | 'parent'; name: string; username: string }
interface Issued { name: string; username: string; password: string; revealUrl?: string; smsNote: string }

const smsLine = (sms: { ok?: boolean; skipped?: boolean; error?: string; recipient?: string } | undefined, phone: string) => {
  if (sms?.ok) return `Sent by SMS to ${sms.recipient || phone || 'the number on file'}.`;
  if (sms?.skipped) return 'No SMS was sent — copy the login below and send it yourself.';
  if (sms?.error) return `The SMS failed (${sms.error}) — copy the login below and send it yourself.`;
  return 'Copy the login below and send it yourself.';
};

export const TripSheet: React.FC<{ f: FleetModel; trip: Trip; top: number; onClose: () => void }> = ({ f, trip: t, top, onClose }) => {
  const action = useAction();
  const { toast } = useToast();
  const [logins, setLogins] = useState<Login[] | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reissue, setReissue] = useState<Login | null>(null);
  const [phone, setPhone] = useState('');
  const [issued, setIssued] = useState<Issued | null>(null);
  const closeBtn = useRef<HTMLButtonElement>(null);
  useEffect(() => { closeBtn.current?.focus(); }, [t.id]);

  /* The trip's temporary accounts are not in the people lists: read them. */
  useEffect(() => {
    let live = true;
    const raw = t.raw;
    const conductors = (Array.isArray(raw.tripConductorUids) ? raw.tripConductorUids : []).map(String);
    const parents = (Array.isArray(raw.parentIds) ? raw.parentIds : []).map(String);
    Promise.all([
      ...conductors.map((uid) => getDoc(doc(db, 'users', uid)).then((s) => (s.exists() ? { uid, kind: 'conductor' as const, d: s.data() } : null)).catch(() => null)),
      ...parents.map((uid) => getDoc(doc(db, 'users', uid)).then((s) => (s.exists() ? { uid, kind: 'parent' as const, d: s.data() } : null)).catch(() => null)),
    ]).then((rows) => {
      if (!live) return;
      setLogins(rows.filter(Boolean).filter((r) => r!.kind === 'conductor' || r!.d.temporaryTripParent === true || String(r!.d.email || '').includes('.trasseapp@'))
        .map((r) => ({ uid: r!.uid, kind: r!.kind, name: String(r!.d.displayName || r!.d.name || r!.d.username || 'Account'), username: String(r!.d.username || r!.d.email || '') })));
    });
    return () => { live = false; };
  }, [t.id, t.raw]);

  const busNames = t.busIds.map((id) => f.buses.find((b) => b.id === id)?.name || id);
  const closed = t.status === 'done' || t.status === 'cancelled';
  const when = t.start ? `${dayMonth(t.start)} · ${hhmm(t.start)}${t.end ? ` → ${t.end.toDateString() === t.start.toDateString() ? hhmm(t.end) : `${dayMonth(t.end)} ${hhmm(t.end)}`}` : ''}` : 'No date yet';

  const doCancel = () => {
    if (busy) return;
    setBusy(true);
    void action({
      busy: 'Cancelling the trip…',
      done: 'Trip cancelled — everyone is out of Trip mode and the children are back on their normal bus.',
      fail: 'Couldn’t cancel the trip. Try again.',
      run: async () => {
        await updateDoc(doc(db, 'schoolTrips', t.id), { status: 'cancelled', cancelledAt: new Date(), updatedAt: serverTimestamp() });
        const parentIds = (Array.isArray(t.raw.parentIds) ? t.raw.parentIds : []).map(String);
        const crew = (Array.isArray(t.raw.tripConductorUids) ? t.raw.tripConductorUids : []).map(String);
        if (!isTripsApiConfigured()) return;
        let parentsCovered = false;
        try {
          const r = await cancelTripApi({ orgCode: f.org.code, tripId: t.id });
          parentsCovered = parentIds.length === 0 || (r?.notified?.total ?? 0) > 0;
        } catch { /* the conductor logins need the backend */ }
        const recipients = parentsCovered ? [...new Set(crew)] : [...new Set([...parentIds, ...crew])];
        if (recipients.length) {
          try {
            await notifyTripParents({ orgCode: f.org.code, tripId: t.id, parentIds: recipients, title: 'School trip cancelled', body: `${t.title || 'The school trip'} has been cancelled. Your child is back on their normal bus.` });
          } catch { /* push needs the backend */ }
        }
      },
    }).then((r) => { setBusy(false); setCancelling(false); if (r !== undefined) onClose(); });
  };

  const doReissue = () => {
    if (!reissue || busy) return;
    setBusy(true);
    const args = { orgCode: f.org.code, tripId: t.id, uid: reissue.uid, username: reissue.username || undefined, phone: phone.trim() || undefined };
    const p = reissue.kind === 'parent'
      ? reissueTripParent(args).then((r) => ({ who: r.parent }))
      : reissueTripConductor(args).then((r) => ({ who: r.conductor }));
    p.then(({ who }) => {
      setIssued({ name: who.displayName || reissue.name, username: who.username, password: who.password, revealUrl: who.revealUrl, smsNote: smsLine(who.sms, phone) });
      setReissue(null); setPhone('');
    })
      .catch((e: Error) => toast({ kind: 'error', text: e.message || 'Couldn’t make a new password.' }))
      .finally(() => setBusy(false));
  };

  const copy = (s: string) => { void navigator.clipboard?.writeText(s).then(() => toast({ kind: 'success', text: 'Copied' })); };

  return (
    <aside className="sheet" aria-label={t.title} style={{ top }}>
      <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--line)', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span className="mono">{when}</span>
          <h2 className="h2" style={{ fontSize: 24, marginTop: 4 }}>{t.title}</h2>
          <div className="row" style={{ gap: 8, marginTop: 8 }}><Chip tone={TONE[t.status]}>{t.statusLabel}</Chip></div>
        </div>
        <button ref={closeBtn} type="button" className="ibtn" aria-label="Close" onClick={onClose}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      </div>
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 20, overflow: 'auto', flex: 1 }}>
        <div className="kv">
          <div><span>Going to</span><b>{t.destination || '—'}</b></div>
          <div><span>Children</span><b className="num">{t.children != null ? plural(t.children, 'child', 'children') : '—'}</b></div>
          <div style={{ gridColumn: '1 / -1' }}><span>Buses</span><b>{busNames.length ? busNames.join(', ') : 'No bus yet'}</b></div>
        </div>
        <div>
          <h3 className="h3" style={{ marginBottom: 8 }}>Trip logins</h3>
          {logins === null && <p className="sm" style={{ margin: 0, color: 'var(--muted)' }}>Loading…</p>}
          {logins !== null && logins.length === 0 && <p className="sm" style={{ margin: 0, color: 'var(--muted)' }}>This trip has no temporary conductors or outside parents.</p>}
          {logins?.map((l, i) => (
            <div key={l.uid} className="row" style={{ gap: 10, padding: '10px 0', borderTop: i ? '1px solid var(--line)' : undefined }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <b>{l.name}</b>
                <div className="sm" style={{ color: 'var(--muted)' }}>{l.kind === 'conductor' ? 'Conductor' : 'Outside parent'}{l.username ? ` · ${l.username}` : ''}</div>
              </div>
              {!closed && <button type="button" className="btn s b-line" onClick={() => setReissue(l)}>New password</button>}
            </div>
          ))}
        </div>
      </div>
      {!closed && (
        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--line)', display: 'flex', gap: 8 }}>
          <button type="button" className="btn b-line" style={{ flex: 1, color: 'var(--bad)' }} onClick={() => setCancelling(true)}>Cancel trip</button>
        </div>
      )}
      {cancelling && (
        <ConsoleModal title="Cancel this trip?" onClose={() => setCancelling(false)} width={520}
          footer={<ModalButtons cancel="Keep the trip" onCancel={() => setCancelling(false)} primary="Cancel trip" danger busy={busy} onPrimary={doCancel} />}>
          <p style={{ margin: 0, fontSize: 17, lineHeight: '25px' }}>Everyone on it is taken out of Trip mode and told, and the children go back to their normal bus.</p>
        </ConsoleModal>
      )}
      {reissue && (
        <ConsoleModal title={`New password for ${reissue.name}`} onClose={() => setReissue(null)} onSubmit={doReissue} enterHint="make it"
          footer={<ModalButtons onCancel={() => setReissue(null)} primary="Make a new password" busy={busy} onPrimary={doReissue} />}>
          <p style={{ margin: 0, color: 'var(--muted)', fontSize: 15 }}>The old password stops working. We text the new one if we can.</p>
          <ModalField label="Phone for the SMS" hint="Leave empty to use the number on file.">
            {(id, ring) => <input id={id} {...ring} style={fieldInput} inputMode="tel" value={phone} placeholder="0712 345 678" onChange={(e) => setPhone(e.target.value)} />}
          </ModalField>
        </ConsoleModal>
      )}
      {issued && (
        <ConsoleModal title={`${issued.name}’s new login`} onClose={() => setIssued(null)}
          footer={<div style={{ display: 'flex', justifyContent: 'flex-end' }}><button type="button" className="btn b-ink" onClick={() => setIssued(null)}>Done</button></div>}>
          <p style={{ margin: 0, fontSize: 16 }}>{issued.smsNote} It is shown only now.</p>
          <div style={{ borderRadius: 14, background: 'var(--s2)', padding: '4px 16px' }}>
            {[['Username', issued.username], ['Password', issued.password], ...(issued.revealUrl ? [['Link', issued.revealUrl]] : [])].map(([k, v], i) => (
              <div key={k} className="row" style={{ gap: 10, padding: '12px 0', borderTop: i ? '1px solid var(--line)' : undefined }}>
                <span style={{ width: 90, color: 'var(--muted)', fontSize: 14 }}>{k}</span>
                <b className="mono" style={{ flex: 1, fontSize: 15, overflowWrap: 'anywhere' }}>{v}</b>
                <button type="button" className="btn s b-line" onClick={() => copy(v)}>Copy</button>
              </div>
            ))}
          </div>
        </ConsoleModal>
      )}
    </aside>
  );
};
